import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { askText, cancelVoice, dismissVoice, stopVoice, subscribeVoice } from "./estateVoice";

/**
 * Asking out loud, and where the words go.
 * ---------------------------------------------------------------------------
 * The five questions on the estate's menu are the ones worth a row each.
 * Everything else someone wants to see is a word - the name of a machine, a
 * package, a version - and typing it is the slow part, not deciding it. So:
 * press Ctrl+Shift+A, say it, and the word lands in the search field of the
 * page that is open, as if it had been typed there.
 *
 * Ctrl+Shift+A starts it (see SidebarNav). The listening itself belongs to
 * estateVoice, which is a module rather than a component, because the browser
 * puts a permission prompt over the page the first time and whatever started
 * the recogniser can be gone by the time it answers. What is drawn while it
 * listens is the pill below, which hangs off the body and belongs to no menu.
 *
 * What it fills is a React-controlled input, so the value is set through the
 * element's own setter and an `input` event is dispatched after it: assigning
 * `.value` alone changes what is drawn and not what React thinks, and the next
 * keystroke would put the old text back.
 * ---------------------------------------------------------------------------
 */

/* The page's own search field, in the order a page would offer one: the
   stage's search before the findings column's, since the stage is the page.
   The stack list's filter is left out on purpose - it filters the column. */
const SEARCH_FIELDS = [
  ".discovery-cards__search input",
  ".forge-wiki__search input",
  ".appTable input[placeholder*='Search' i]",
  "input[placeholder*='Search' i]",
];

function visible(el) {
  if (!el) return false;
  const box = el.getBoundingClientRect();
  return box.width > 0 && box.height > 0 && getComputedStyle(el).visibility !== "hidden";
}

export function pageSearchField() {
  for (const sel of SEARCH_FIELDS) {
    const found = Array.from(document.querySelectorAll(sel)).find(visible);
    if (found) return found;
  }
  return null;
}

/** Type into a React input from outside React. */
export function typeInto(el, text) {
  const setter = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(el), "value")?.set;
  if (setter) setter.call(el, text); else el.value = text;
  el.dispatchEvent(new Event("input", { bubbles: true }));
}

const MicMark = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="9" y="3" width="6" height="11" rx="3" stroke="currentColor" strokeWidth="1.8" />
    <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M12 18v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

/**
 * What listening looks like, for as long as it lasts.
 *
 * Drawn into the body rather than into whatever started it, so closing the
 * menu - or the browser's own permission prompt closing it - leaves the
 * recogniser and this pill exactly where they were.
 */
export function EstateVoicePill() {
  const [{ state, heard }, setVoice] = useState({ state: "idle", heard: "" });
  const [typed, setTyped] = useState("");
  const field = useRef(null);
  useEffect(() => subscribeVoice(setVoice), []);

  /* A pill that is waiting to be typed into is a pill with the cursor already
     in it: it was opened by a keystroke, and asking for a second one to reach
     the field it put on screen is asking twice. */
  const asking = state !== "idle" && state !== "listening" && state !== "working";
  useEffect(() => {
    if (!asking) { setTyped(""); return; }
    const at = setTimeout(() => field.current?.focus(), 30);
    return () => clearTimeout(at);
  }, [asking]);

  /* Escape gives up on it; Enter takes what has been heard so far. */
  useEffect(() => {
    if (state !== "listening") return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); cancelVoice(); }
      if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); stopVoice(); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [state]);

  if (state === "idle") return null;

  /* While nothing has been heard yet, the pill says what to do with it - the
     stop button is the only thing that ends a sitting now, so it has to be
     plain that it is there and that it is waiting.

     And where speech cannot happen at all, the pill says why in one line and
     then gets out of the way of the field underneath it: the ask is the
     question, not the microphone. */
  const say = {
    listening: heard || "Listening — press Stop when you are done",
    working: heard || "thinking…",
    typing: "What would you like to see?",
    denied: "The microphone is blocked for this site — type it instead",
    failed: "The microphone would not start — type it instead",
    offline: "Chrome recognises speech on its own service, which this browser cannot reach — type it instead",
  }[state];

  return createPortal(
    <div className={`estate-voice is-${state}`} role="status" aria-live="polite">
      <span className="estate-voice__mark" aria-hidden="true"><MicMark /></span>
      <span className="estate-voice__said">{say}</span>
      {asking && (
        <form
          className="estate-voice__ask"
          onSubmit={(e) => { e.preventDefault(); if (askText(typed)) setTyped(""); }}
        >
          <input
            ref={field}
            type="text"
            className="estate-voice__type"
            value={typed}
            placeholder="show critical machines"
            aria-label="What would you like to see?"
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => {
              /* the pill is over the page: its own keys stay in it */
              e.stopPropagation();
              if (e.key === "Escape") { e.preventDefault(); dismissVoice(); }
            }}
          />
          <button type="submit" className="estate-voice__go" disabled={!typed.trim()}>Go</button>
        </form>
      )}

      {state === "listening" && (
        <>
          <span className="estate-voice__level" aria-hidden="true"><i /><i /><i /></span>
          {/* The way out, with its name on it: an icon at 60% opacity was a
              button people did not know they had, and the only other way to
              finish is a key nobody has been told about. */}
          <button
            type="button"
            className="estate-voice__stop"
            onClick={() => stopVoice()}
            title="Stop listening and use what you said (Enter)"
            aria-label="Stop listening"
          >
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <rect x="7" y="7" width="10" height="10" rx="2" fill="currentColor" />
            </svg>
            Stop
          </button>
        </>
      )}
    </div>,
    document.body,
  );
}
